import { findLastIndex } from 'helpers/find-last-index';
import { StoryBoard } from 'services/storyboard.service';
import {
  EMPTY_CELL,
  LinkedClips,
  LinkedGroup,
  LinkedGroupId,
  ClipsMap,
  UNLINKED_ID,
} from 'components/app/editor/_mediator/editor.slice';
import { SbPosition, SB_POSITION } from 'components/app/editor/_mediator/editor-controls.slice';
import { AddClipAction } from 'components/app/editor/_mediator/editor-actions.slice';
import {
  findSbPosition,
  getSbBlank,
  getSbCopy,
  getSbPosition,
  getSbTrackIndex,
  getSbTracks,
  isNull,
  selectClipData,
} from 'components/app/_helpers/_app.helper';
import { selectLinkedGroup } from './editor-clips-linking.helper';

const ERR_ID = 'editor-clips-sb.helper.ts';

export const sbApi = (sb: StoryBoard) => ({
  findLinkedClipsPosition: (from: SbPosition, len: number) => {
    const tracks = getSbTracks();
    const trackIndex = getSbTrackIndex(from.track);
    let result: SbPosition | null = null;
    tracks.slice(trackIndex).find((track) => {
      let currCellIndex = track === from.track ? from.cell : 0;
      let cellsLength = 0;
      while (cellsLength < len && currCellIndex < sb[track].length) {
        if (sb[track][currCellIndex] === EMPTY_CELL) cellsLength++;
        else cellsLength = 0;
        currCellIndex++;
      }
      if (cellsLength === len) {
        result = { track, cell: currCellIndex - len };
        return true;
      }
    });
    return result;
  },
  validateAddClipPosition: (position: SbPosition, action: AddClipAction) => {
    const track = sb[position.track];
    if (track[position.cell] === EMPTY_CELL) {
      if (action !== 'split' || track[position.cell + 1] === EMPTY_CELL) {
        return position;
      }
    }
  },
  getBrokenLinks: (links: LinkedClips): LinkedGroupId[] => {
    const brokenLinks: LinkedGroupId[] = [];
    Object.keys(links).forEach((_linkId) => {
      const linkId = Number(_linkId);
      const linkedGroup = selectLinkedGroup(linkId, links);
      const position = getSbPosition(linkedGroup[0], sb);
      if (linkedGroup.find((clipId, index) => clipId !== sb[position.track]![position.cell + index]))
        brokenLinks.push(linkId);
    });
    return brokenLinks;
  },
  removeClip: (clipId: ClipId, cellPosition?: SbPosition) => {
    const position = cellPosition ?? findSbPosition(clipId, sb);
    if (!position) {
      console.log(`[${ERR_ID}] removeSbClip`);
      return sb;
    }
    const res: Partial<StoryBoard> = {};
    res[position.track] = [...sb[position.track]];
    res[position.track]![position.cell] = EMPTY_CELL;
    return res;
  },
  init: (markersLen: number) => {
    const boards = { ...sb };
    getSbTracks().forEach((trackId) => {
      const diff = markersLen + 1 - boards[trackId].length;
      if (diff > 0) boards[trackId] = [...boards[trackId], ...Array.from({ length: diff }, () => EMPTY_CELL)];
      else if (diff < 0) boards[trackId].splice(boards[trackId].length + diff);
    });
    return boards;
  },
  addMarkers: (markers: Index[]) => {
    const res = getSbCopy(sb);
    markers.sort((a, b) => a - b);
    markers.forEach((cell, i) => {
      getSbTracks().forEach((trackId) => {
        res[trackId].splice(cell + 1 + i, 0, EMPTY_CELL);
      });
    });
    return res;
  },
});

export const sliceSbTrack = (track: CellClip[]) => {
  const index = findLastIndex(track, (el) => el !== EMPTY_CELL);
  if (index === -1) return [];
  return track.slice(0, index + 1);
};

/* Handle Broken Links */

type Status = 'noBrokenLinks' | 'cantBeFixed' | 'success';
type HandleSbLinksRes = { storyBoard: Partial<StoryBoard>; status: Status; processedClips: ClipId[] };
type ClipPositionsMap = Map<ClipId, SbPosition>;

export const handleSbLinks = (
  linkedClips: LinkedClips,
  storyBoardUpdated: Partial<StoryBoard>,
  storyBoardOrigin: StoryBoard
): HandleSbLinksRes => {
  const storyBoard: StoryBoard = { ...storyBoardOrigin, ...storyBoardUpdated };
  const storyBoardRes: StoryBoard = { storyBoard: [], clipBoard: [], discardBoard: [] };
  const clipPositions: ClipPositionsMap = new Map();

  const findPositionForBrokenLink = (linkId: LinkedGroupId): SbPosition | null => {
    const { findLinkedClipsPosition } = sbApi(storyBoardRes);
    const linkedGroup = selectLinkedGroup(linkId, linkedClips);
    const startFrom = linkedGroup.reduce((accPos, curr, i) => {
      if (i === 0) return accPos;
      const currPos = clipPositions.get(curr);
      const accTrackIndex = getSbTrackIndex(accPos.track);
      const currTrackIndex = getSbTrackIndex(currPos!.track);
      if (accTrackIndex > currTrackIndex) return currPos!;
      if (accTrackIndex === currTrackIndex && accPos.cell > currPos!.cell) return currPos!;
      return accPos;
    }, clipPositions.get(linkedGroup[0])!);
    return (
      findLinkedClipsPosition(startFrom!, linkedGroup.length) ||
      findLinkedClipsPosition(SB_POSITION, linkedGroup.length)
    );
  };

  const updateStoryboard = (linkedGroup: LinkedGroup, newLinkPosition: SbPosition) => {
    linkedGroup.forEach((clipId, index) => {
      storyBoardRes[newLinkPosition.track][newLinkPosition.cell + index] = clipId;
    });
  };

  const res = (status: Status) => {
    if (status === 'success') return { storyBoard: storyBoardRes, status, processedClips: Array.from(brokenClips) };
    return { storyBoard: storyBoardUpdated, status, processedClips: [] };
  };

  if (isNull(linkedClips)) {
    console.error(`[${ERR_ID}] handleSbLinks: linkedGroups are not defined`);
    return res('cantBeFixed');
  }

  const brokenLinks = sbApi(storyBoard).getBrokenLinks(linkedClips);
  if (brokenLinks.length === 0) return res('noBrokenLinks');

  const brokenClips: Set<ClipId> = new Set(brokenLinks.flatMap((linkId) => selectLinkedGroup(linkId, linkedClips)));

  getSbTracks().forEach((track) => {
    storyBoard[track].forEach((cellClip, index) => {
      storyBoardRes[track].push(cellClip);
      if (cellClip !== EMPTY_CELL && brokenClips.has(cellClip)) {
        clipPositions.set(cellClip, { track, cell: index });
        storyBoardRes[track][index] = EMPTY_CELL;
      }
    });
  });

  const error = brokenLinks.some((linkId) => {
    const newLinkPosition = findPositionForBrokenLink(linkId);
    if (newLinkPosition) updateStoryboard(selectLinkedGroup(linkId, linkedClips), newLinkPosition);
    else return true;
  });

  if (error) return res('cantBeFixed');
  return res('success');
};

/* Handle Deleted Markers */

const displaceLinkedClips = (delMarkers: Index[], sb: StoryBoard, clipsMap: ClipsMap) => {
  const linksSet = new Set<number>();
  const markersSet = new Set(delMarkers);
  const clipsToDisplace: ClipId[] = [];
  let count = 0;

  const displace = () => {
    const sbRes = getSbBlank();
    getSbTracks().forEach((trackId) => {
      sb[trackId].forEach((cell, index) => {
        const linkId = cell !== EMPTY_CELL ? selectClipData(cell, clipsMap).clipGroupId : UNLINKED_ID;
        if (markersSet.has(index - 1)) {
          if (linkId !== UNLINKED_ID) {
            linksSet.add(linkId);
            clipsToDisplace.push(cell!);
            sbRes[trackId].push(EMPTY_CELL);
          } else sbRes[trackId].push(cell);
        } else {
          if (count === clipsToDisplace.length) sbRes[trackId].push(cell);
          else if (cell !== EMPTY_CELL && !linksSet.has(linkId)) sbRes[trackId].push(cell);
          else {
            if (linksSet.has(linkId)) clipsToDisplace.push(cell!);
            sbRes[trackId].push(clipsToDisplace[count]);
            count++;
          }
        }
      });
    });
    if (count === clipsToDisplace.length) return sbRes;
  };

  return displace() ?? displace();
};

const displaceClips = (delMarkers: Index[], sb: StoryBoard) => {
  const sbRes = getSbBlank();
  const markersSet = new Set(delMarkers);
  const clipsToDisplace: ClipId[] = [];
  let count = 0;

  sb.storyBoard.forEach((_, i) => {
    getSbTracks().forEach((track) => {
      const clipId = sb[track][i];
      const isClipId = clipId !== EMPTY_CELL;
      if (markersSet.has(i - 1)) {
        if (isClipId) clipsToDisplace.push(clipId);
      } else {
        if (isClipId || count === clipsToDisplace.length) sbRes[track].push(clipId);
        else {
          sbRes[track].push(clipsToDisplace[count]);
          count++;
        }
      }
    });
  });
  if (count === clipsToDisplace.length) return sbRes;

  for (let i = 0; i < sb.storyBoard.length && count < clipsToDisplace.length; i++) {
    for (const track of getSbTracks()) {
      if (sbRes[track][i] === EMPTY_CELL && count < clipsToDisplace.length) {
        sbRes[track][i] = clipsToDisplace[count++];
      }
    }
  }
  if (count === clipsToDisplace.length) return sbRes;
};

export const handleSbDelMarkers = (delMarkers: Index[], sb: StoryBoard, clipsMap: ClipsMap) => {
  if (delMarkers.length === 0) return sb;
  const _sb = displaceLinkedClips(delMarkers, sb, clipsMap);
  if (_sb) return displaceClips(delMarkers, _sb);
};
